import moment from 'moment';
import { useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

const PLACEHOLDER_IMAGE = '/img/image-not-available.png';
const PLACEHOLDER_IMAGE_INSTAGRAM = '/img/story-not-available.png';

// Runs shorter than this keep their normal bubbles.
const MIN_RUN_LENGTH = 3;
const MAX_GAP_MS = 2 * 60 * 1000;
const TILE_GAP_PX = 6;
const NON_VISUAL_EXTENSIONS = ['.pdf', '.txt', '.csv', '.amr', '.mp3', '.m4a'];
const VIDEO_EXTENSIONS = ['.mov', '.mp4'];

const isVideo = (url) => VIDEO_EXTENSIONS.some((ext) => url.includes(ext));

const STRIP_SENDERS = ['customer', 'business'];
const UNDELIVERED_SMS_STATUSES = ['failed', 'undelivered'];

// Twilio's order; a strip reports the photo furthest behind, so "delivered" means all of them.
const SMS_STATUS_ORDER = ['accepted', 'queued', 'sending', 'sent', 'delivered', 'read'];
const CHANNEL_LABELS = { email: ' via email', phone: ' via sms', webchat: ' via website widget' };

const getStripStatus = (messages) => {
  const statuses = messages.map((m) => m.SmsStatus).filter(Boolean);
  if (!statuses.length) return '';
  return statuses.reduce((behind, status) =>
    SMS_STATUS_ORDER.indexOf(status) < SMS_STATUS_ORDER.indexOf(behind) ? status : behind,
  );
};

const idOf = (value) => (value && typeof value === 'object' ? value._id : value)?.toString() ?? '';

// A message that is only a photo or video (captions keep their bubble). Business
// photos still sending or not delivered keep their bubble so retry and the error stay visible.
const isStripMedia = (message) =>
  STRIP_SENDERS.includes(message?.sender) &&
  typeof message.media === 'string' &&
  message.media &&
  !message.text?.trim() &&
  !NON_VISUAL_EXTENSIONS.some((ext) => message.media.includes(ext)) &&
  message.status !== 'sending' &&
  message.status !== 'failed' &&
  !message.SmsErrorCode &&
  !UNDELIVERED_SMS_STATUSES.includes(message.SmsStatus);

// Same person: the customer (group participants differ), or the same teammate or automation.
const isSameSender = (a, b) =>
  a.sender === b.sender &&
  idOf(a._customer) === idOf(b._customer) &&
  idOf(a._user) === idOf(b._user) &&
  idOf(a._messageAutomation) === idOf(b._messageAutomation);

// Status lines (AI on/off, tags, assignment) that automations can log between
// two photos. Calls are real conversation, so they still end a run.
const isPassiveEvent = (message) => message?.sender === 'event' && !message._call;

// Grouped by send time, like Apple Messages: each photo joins the run if it
// arrived within MAX_GAP_MS of the previous photo on the same day.
const continuesRun = (prev, next) => {
  const gap = new Date(next.createdAt) - new Date(prev.createdAt);
  return gap >= 0 && gap <= MAX_GAP_MS && moment(prev.createdAt).isSame(next.createdAt, 'day');
};

/**
 * Collapses photo/video messages one person sent close together into one item.
 * Status lines logged in between move to just after the strip.
 * Returns `[{ message, mediaGroup? }]`, where `message` is the first message of
 * the run so row keys stay stable as more photos arrive.
 */
export const groupMediaRuns = (messages) => {
  const items = [];
  let i = 0;
  while (i < messages.length) {
    const message = messages[i];
    if (!isStripMedia(message)) {
      items.push({ message });
      i += 1;
      continue;
    }
    const run = [message];
    const movedEvents = [];
    let pendingEvents = [];
    let end = i + 1;
    while (end < messages.length) {
      const next = messages[end];
      const last = run[run.length - 1];
      if (isPassiveEvent(next) && continuesRun(last, next)) {
        pendingEvents.push(next);
      } else if (isStripMedia(next) && isSameSender(last, next) && continuesRun(last, next)) {
        run.push(next);
        movedEvents.push(...pendingEvents);
        pendingEvents = [];
      } else {
        break;
      }
      end += 1;
    }
    // Status lines after the last photo stay where they are.
    end -= pendingEvents.length;
    if (run.length >= MIN_RUN_LENGTH) {
      items.push({ message, mediaGroup: run }, ...movedEvents.map((m) => ({ message: m })));
    } else {
      items.push(...messages.slice(i, end).map((m) => ({ message: m })));
    }
    i = end;
  }
  return items;
};

const handleImageError = (e) => {
  const fallback = e.currentTarget.src.includes('ig_messaging')
    ? PLACEHOLDER_IMAGE_INSTAGRAM
    : PLACEHOLDER_IMAGE;
  e.currentTarget.onerror = null;
  e.currentTarget.src = fallback;
};

// How many 3:4 tiles fit across the strip (px-3 padding, gap-1.5).
const countFittingTiles = (list) => {
  const tileWidth = (list.clientHeight * 3) / 4;
  return Math.max(1, Math.floor((list.clientWidth - 24 + TILE_GAP_PX) / (tileWidth + TILE_GAP_PX)));
};

const MediaStrip = ({ messages, sentBy, showImageLightbox }) => {
  const listRef = useRef(null);
  const [fitCount, setFitCount] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    const update = () => setFitCount(countFittingTiles(list));
    update();
    const observer = new ResizeObserver(update);
    observer.observe(list);
    return () => observer.disconnect();
  }, []);

  const images = messages.map((m) => m.media).filter((url) => !isVideo(url));
  const last = messages[messages.length - 1];
  const fromBusiness = messages[0].sender !== 'customer';
  const status = getStripStatus(messages);
  const collapsed = !expanded && fitCount !== null && messages.length > fitCount;
  const shown = collapsed ? messages.slice(0, fitCount) : messages;
  const moreCount = messages.length - fitCount + 1;

  // Photos open in the lightbox; videos can't, so a hidden video shows the full strip instead.
  const openMore = () => {
    const hidden = messages.slice(fitCount - 1);
    if (hidden.some((m) => isVideo(m.media))) {
      setExpanded(true);
    } else {
      showImageLightbox(hidden[0].media, images);
    }
  };
  const videoCount = messages.length - images.length;
  const label = [
    images.length && `${images.length} ${images.length === 1 ? 'photo' : 'photos'}`,
    videoCount && `${videoCount} ${videoCount === 1 ? 'video' : 'videos'}`,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="py-1.5">
      <ul
        ref={listRef}
        aria-label={label}
        className={cn(
          'flex h-[152px] gap-1.5 px-3 md:h-48',
          // auto margin right-aligns the tiles, and drops to 0 once they overflow and scroll
          fromBusiness && '[&>li:first-child]:ml-auto',
          collapsed
            ? 'overflow-hidden'
            : 'overflow-x-auto overscroll-x-contain snap-x scrollbar-thin',
        )}
      >
        {shown.map((message, index) => {
          const url = message.media;
          if (collapsed && index === shown.length - 1) {
            return (
              <li
                key={message._id || url}
                className="relative h-full aspect-[3/4] shrink-0 overflow-hidden rounded-xl bg-black"
              >
                <button
                  type="button"
                  aria-label={`Show ${moreCount} more`}
                  onClick={openMore}
                  className="size-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                >
                  {isVideo(url) ? (
                    <video muted preload="metadata" className="size-full object-cover" src={url} />
                  ) : (
                    <img
                      src={url}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      onError={handleImageError}
                      className="size-full object-cover"
                    />
                  )}
                  <span className="absolute inset-0 flex items-center justify-center bg-black/55 text-2xl font-semibold text-white">
                    +{moreCount}
                  </span>
                </button>
              </li>
            );
          }
          return (
            <li
              key={message._id || url}
              className={cn(
                'relative h-full aspect-[3/4] shrink-0 snap-start overflow-hidden rounded-xl',
                isVideo(url) ? 'bg-black' : 'bg-muted/30',
              )}
            >
              {isVideo(url) ? (
                // biome-ignore lint/a11y/useMediaCaption: texted media has no captions
                <video controls preload="metadata" className="size-full object-cover" src={url} />
              ) : (
                <button
                  type="button"
                  aria-label={`Open photo ${images.indexOf(url) + 1} of ${images.length}`}
                  onClick={() => showImageLightbox(url, images)}
                  className="size-full cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                >
                  <img
                    src={url}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    onError={handleImageError}
                    className="size-full object-cover"
                  />
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <div
        className={cn(
          'px-4 pt-1 text-[11px] leading-4 text-muted-foreground',
          fromBusiness && 'text-right',
        )}
      >
        {`${sentBy} ${moment(last.createdAt).format('LT')}${status ? ` - ${status}` : ''}${CHANNEL_LABELS[last.channel] || ''} · ${label}`}
      </div>
    </div>
  );
};

export default MediaStrip;
